/**
 * Configuration the browser reads, delivered at RUNTIME.
 *
 * In the Next shell every one of these was a `NEXT_PUBLIC_*` env var, which Next inlines
 * into the bundle at build time. That is not a detail — it is the direct cause of a
 * production incident this team already had: `NEXT_PUBLIC_DEFECTS_LOG_API_URL` was absent
 * from the env blob when the image was built, so the bundle shipped the remote as
 * `defects-log-app@undefined/remoteEntry.js`, and no runtime Secret could fix it because
 * the value was a string literal in the JavaScript. The route was live and broken and the
 * only remedy was a rebuild.
 *
 * So the values below are not compiled in. index.html loads `/config.js`, which sets
 * `window.__SHELL_CONFIG__`: Express builds it from the pod's environment (server/index.ts),
 * and the frontend deploy writes it per environment for S3. Changing one is a restart or a
 * redeploy of that one file, not a rebuild.
 *
 * The one thing that CANNOT move here is the remote origins: they go into the federation
 * config, which the bundler needs at build time. Those keep the build-time guard in
 * rspack.config.js instead, which fails the build rather than shipping "undefined".
 */

export interface ShellRuntimeConfig {
  /** Source commit SHA of this build. Stamped on every client log line. */
  buildId: string;
  /** 'production' | 'development' — the DEPLOYMENT's environment, not the bundle mode. */
  nodeEnv: string;
  posthogKey?: string;
  posthogHost?: string;
  posthogEnv?: string;
  /** false suppresses email/name in PostHog identify. Defaults to masking. */
  posthogMaskPII: boolean;
  /** Ship client log batches to /api/client-logs. On in production. */
  shipClientLogs: boolean;
  /** Overrides the tenant-derived sign-in origin. Rarely set. */
  loginOrigin?: string;
}

const FALLBACK: ShellRuntimeConfig = {
  buildId: 'unknown',
  nodeEnv: 'development',
  posthogMaskPII: true,
  shipClientLogs: false,
};

declare global {
  interface Window {
    __SHELL_CONFIG__?: Partial<ShellRuntimeConfig>;
  }
}

/**
 * Read once, at module load.
 *
 * index.html loads /config.js with a blocking script ahead of the bundle, so it is always
 * present by the time this runs. The fallback is for the one case where it is not — a stale index.html
 * served from somewhere that predates this — and it fails SAFE: no analytics key, no log
 * shipping, PII masked.
 */
export const config: ShellRuntimeConfig = {
  ...FALLBACK,
  ...(typeof window !== 'undefined' ? window.__SHELL_CONFIG__ ?? {} : {}),
};

/** `true` when this is a production DEPLOYMENT, whatever the bundle was compiled as. */
export const isProduction = config.nodeEnv === 'production';
